import classNames from 'classnames';
import { isNaN, omit, size, toNumber } from 'lodash';
import { InputHTMLAttributes, memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import useAutosizeTextArea from '../../../common/hooks/use-auto-size-textarea';
import { QuestionType } from '../../../report/types';
import { QuestionProps } from '../../pa-prop-types';
import { propsToIgnore } from './qs-const';
import { AthenaServices } from '../../../lib/athena/athena-services';

// surveyState를 context로 가져오기 위한 import
import { useSurveyState } from '../../hooks/use-survey-state';
import { useAutocomplete } from './hooks/useAutocomplete';
import { useStaticLocales } from '../../../admin/hooks/locales/use-static-locale';

function QsTextarea(
  props: InputHTMLAttributes<HTMLTextAreaElement> &
    QuestionProps & {
      question: QuestionType;
      onUpdate: (value?: string) => void;
    }
) {
  const { onUpdate, className, value, question, error, ...rest } = props;
  const { typeOptions } = question || {};
  const { max } = typeOptions || {};
  const maxLength = toNumber(max);

  const { sltStr } = useStaticLocales();

  // surveyState context 사용
  const [{ surveyState }, { setSurveyState }] = useSurveyState();

  // q-dhx-description 질문 여부
  const isDescriptionQuestion = useMemo(() => {
    return question?.questionKey === 'q-dhx-description-athena' || question?.questionKey === 'q-dhx-description';
  }, [question?.questionKey]);

  const isPharmacyQuestion = useMemo(() => {
    return question?.questionKey === "q-pharamcy-info-athena" || question?.questionKey === "q-pharamcy-info";
  }, [question?.questionKey]);

  const isPCPQuestion = useMemo(() => {
    return question?.questionKey === "q-primary-care-doctor-info-athena" || question?.questionKey === "q-primary-care-doctor-info";
  }, [question?.questionKey]);

  // 자동완성 관련 상태
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [autocompleteItems, setAutocompleteItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSelectedItem, setHasSelectedItem] = useState(false);
  const [providers, setProviders] = useState<any[]>([]);
  const [existPharmacy, setExistPharmacy] = useState<any>(null);
  const [existPCP, setExistPCP] = useState<any[]>([]);

  const [checkedPharmacy, setCheckedPharmacy] = useState(false);
  const [checkedPCP, setCheckedPCP] = useState(false);
  const [checkedCurrentMedication, setCheckedCurrentMedication] = useState(false);
  const [existCurrentMedication, setExistCurrentMedication] = useState<any[]>([]);

  const checkMedication = useCallback(async () => {
    if (isDescriptionQuestion && !checkedCurrentMedication) {
      const patientId = surveyState?.patientDetails?.patientid;
      const departmentId = surveyState?.patientDetails?.departmentid;
      
      // patientId와 departmentId가 유효한 값인지 확인
      if (!patientId || !departmentId || patientId === 'undefined' || departmentId === 'undefined') {
        setCheckedCurrentMedication(true);
        return;
      }
      
      const result = await AthenaServices.findExistMedication(patientId, departmentId);
      setExistCurrentMedication(result.data.medications);
      setCheckedCurrentMedication(true);
    }
  }, [isDescriptionQuestion, checkedCurrentMedication, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const checkPCP = useCallback(async () => {
    if (isPCPQuestion && !checkedPCP) {
      const patientId = surveyState?.patientDetails?.patientid;
      const departmentId = surveyState?.patientDetails?.departmentid;
      
      // patientId와 departmentId가 유효한 값인지 확인
      if (!patientId || !departmentId || patientId === 'undefined' || departmentId === 'undefined') {
        setCheckedPCP(true);
        return;
      }
      
      const result = await AthenaServices.findExistProvider(patientId, departmentId);
      
      setExistPCP(result.data.members);
      setCheckedPCP(true);
    }
  }, [isPCPQuestion, checkedPCP, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const checkPharmacy = useCallback(async () => {
    if (isPharmacyQuestion && !checkedPharmacy) {
      const patientId = surveyState?.patientDetails?.patientid;
      const departmentId = surveyState?.patientDetails?.departmentid;
      
      // patientId와 departmentId가 유효한 값인지 확인
      if (!patientId || !departmentId || patientId === 'undefined' || departmentId === 'undefined') {
        setCheckedPharmacy(true);
        return;
      }
      
      const result = await AthenaServices.findExistPharmacy(patientId, departmentId);
      setExistPharmacy(result.data);
      setCheckedPharmacy(true);
    }
  }, [isPharmacyQuestion, checkedPharmacy, surveyState?.patientDetails?.patientid, surveyState?.patientDetails?.departmentid]);

  const [valueTemp, setValueTemp] = useState(`${value}` || '');
  const [valueDebbed] = useDebounce(valueTemp, 300);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef, `${valueTemp}`);

  const textLen = size(`${valueTemp}`);

  useEffect(() => {
    if (isPharmacyQuestion) {
      if (existPharmacy) {
        // existPharmacy의 첫 번째 항목이 있으면 valueTemp에 해당 약국 이름과 주소를 채워넣음
        const firstPharmacy = existPharmacy;
          // clinicalprovidername에서 # 뒤에 있는 숫자는 모두 제거
          let pharmacyName = firstPharmacy.clinicalprovidername || firstPharmacy.pharmacyName || '';
          if (pharmacyName) {
            pharmacyName = pharmacyName.replace(/\s*#\d+/g, '');
          }
          
          // 주소 정보 추가
          const address = firstPharmacy.address ? `${firstPharmacy.address}, ${firstPharmacy.city}, ${firstPharmacy.state}` : '';
          const fullValue = address ? `${pharmacyName}, ${address}` : pharmacyName;
          
          setValueTemp(fullValue);
          onUpdate(fullValue);
          setShowAutocomplete(false);
          setHasSelectedItem(true);

          setSurveyState((prev: any) => ({
            ...prev,
            pharmacyId: firstPharmacy.clinicalproviderid,
          }));
      }
    }
  }, [existPharmacy])

  useEffect(() => {
    if (isPCPQuestion) {
      if (existPCP.length > 0) {
        if (existPCP && existPCP.length > 0) {
          // recipientclass.code가 3인 PCP만 선택
          const firstPCP = existPCP.find((pcp: any) => pcp.recipientclass?.code === 3);
          const name = firstPCP.name || firstPCP.providerName || '';
          
          // 주소 정보 추가
          const address = firstPCP.address ? `${firstPCP.address}, ${firstPCP.city}, ${firstPCP.state}` : '';
          const fullValue = address ? `${name}\n${address}` : name;
          
          setValueTemp(fullValue);
          onUpdate(fullValue);

          setSurveyState((prev: any) => ({
            ...prev,
            providerId: firstPCP.clinicalproviderid,
          }));

          setShowAutocomplete(false);
          setHasSelectedItem(true);
        }
      }
    }
  }, [existPCP]) 
  

  useEffect(() => {
    if (isDescriptionQuestion) {
      if (existCurrentMedication.length > 0) {
        setSurveyState((prev: any) => ({
          ...prev,
          currentMedication: existCurrentMedication[existCurrentMedication.length - 1][0].medication || '',
        }));
        setValueTemp(existCurrentMedication[existCurrentMedication.length - 1][0].medication || '');
        setShowAutocomplete(false);
        setHasSelectedItem(true);
      }
    }
  }, [existCurrentMedication]);

  useEffect(() => {
    if (surveyState?.checkFlag && !checkedPharmacy && isPharmacyQuestion) {
      checkPharmacy();
    } 
    
    if (surveyState?.checkFlag && !checkedPCP && isPCPQuestion) {
      checkPCP();
    }

    if (surveyState?.checkFlag && !checkedCurrentMedication && isDescriptionQuestion) {
      checkMedication();
    }
  }, [surveyState?.checkFlag, checkedPharmacy, checkedPCP, checkedCurrentMedication, isPharmacyQuestion, isPCPQuestion, isDescriptionQuestion, checkPharmacy, checkPCP, checkMedication]);
  // valueDebbed가 바뀔 때 onUpdate 호출
  useEffect(() => {
    if (value !== valueDebbed) {
      onUpdate(valueDebbed);
      
      // Past Medical History (q-dhx-description-athena)는 currentMedication에만 설정
      // medicationCurrent에는 설정하지 않음 (Regular Medication과 구분)
      if (isDescriptionQuestion) {
        setSurveyState((prev: any) => ({
          ...prev,
          currentMedication: valueDebbed || '',
          // medicationCurrent는 설정하지 않음 - Regular Medication에서만 사용
        }));
      }
    }
  }, [valueDebbed, isDescriptionQuestion, setSurveyState]);

  useEffect(() => {
    if (isDescriptionQuestion) {
      if (existCurrentMedication) {
        setSurveyState((prev: any) => ({
          ...prev,
          currentMedication: existCurrentMedication || '',
        }));
      }
    }
  }, [existCurrentMedication]);


  // 이전 검색 요청 취소를 위한 ref
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // PCP와 Pharmacy 검색을 위한 디바운스된 값 (적절한 대기 시간)
  const [searchDebounced] = useDebounce(valueTemp, 500); // 500ms로 설정하여 타이핑 중 불필요한 요청 방지

  // PCP와 Pharmacy 검색 실행
  useEffect(() => {
    // 이미 아이템을 선택했거나 검색어가 없으면 검색하지 않음
    if (hasSelectedItem || !searchDebounced || !(isPCPQuestion || isPharmacyQuestion)) {
      setAutocompleteItems([]);
      setShowAutocomplete(false);
      setIsLoading(false);
      return undefined; // cleanup 함수가 없는 경우 명시적으로 undefined 반환
    }

    const performSearch = async () => {
      // 검색어 최소 길이를 3자로 증가 (불필요한 요청 방지)
      if (searchDebounced.trim().length < 3) {
        setAutocompleteItems([]);
        setShowAutocomplete(false);
        setIsLoading(false);
        return;
      }

      // 이전 요청 취소
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 새로운 AbortController 생성
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // 검색 시작 시 즉시 로딩 상태 표시
      setIsLoading(true);
      setShowAutocomplete(true); // 검색 시작 시 즉시 자동완성 영역 표시
      
      try {
        let results;
        const zip = surveyState?.patientDetails?.zip || '';
        
        // zip 코드가 유효한지 확인
        if (!zip || zip === 'undefined' || zip === 'null') {
          setAutocompleteItems([]);
          setShowAutocomplete(false);
          setIsLoading(false);
          return;
        }
        
        
        // AbortSignal을 전달하여 요청 취소 지원
        if (isPCPQuestion) {
          results = await AthenaServices.searchProvider(zip, searchDebounced.trim(), 'CONSULT', abortController.signal);
        } else if (isPharmacyQuestion) {
          results = await AthenaServices.searchProvider(zip, searchDebounced.trim(), 'PRESCRIPTION', abortController.signal);
        }

        // 요청이 취소되었는지 확인
        if (abortController.signal.aborted) {
          return;
        }

        
        if (results && results.data?.clinicalproviders?.length > 0) {
          setAutocompleteItems(results.data.clinicalproviders);
          setSelectedIndex(-1);
        } else {
          setAutocompleteItems([]);
        }
      } catch (error1: any) {
        // AbortError는 무시 (의도적인 취소)
        if (error1?.name === 'AbortError' || error1?.message?.includes('aborted')) {
          return;
        }
        setAutocompleteItems([]);
      } finally {
        // 요청이 취소되지 않았을 때만 로딩 상태 해제
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    performSearch();

    // cleanup: 컴포넌트 언마운트 시 요청 취소
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchDebounced, isPCPQuestion, isPharmacyQuestion, surveyState?.patientDetails?.zip, hasSelectedItem]);

  // 키보드 네비게이션 핸들러
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showAutocomplete || autocompleteItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < autocompleteItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : autocompleteItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < autocompleteItems.length) {
          handleSelectItem(autocompleteItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowAutocomplete(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // 아이템 선택 핸들러
  const handleSelectItem = (item: any) => {
    let selectedValue = '';
    if (isPCPQuestion) {
      const name = item.name || item.providerName || '';
      const address = item.address ? `${item.address}, ${item.city}, ${item.state}` : '';
      selectedValue = address ? `${name}, ${address}` : name;
      
      // PCP ID 설정
      if (item.clinicalproviderid) {
        setSurveyState((prev: any) => ({
          ...prev,
          providerId: item.clinicalproviderid,
        }));
      }
    } else if (isPharmacyQuestion) {
      // 약국명에서 마지막에 #12345와 같은 번호 패턴을 제거
      const rawName = item.name || item.pharmacyName || '';
      const name = rawName.replace(/\s*#\d+$/, '');
      const address = item.address ? `${item.address}, ${item.city}, ${item.state}` : '';
      selectedValue = address ? `${name}, ${address}` : name;
      
      // Pharmacy ID 설정
      if (item.clinicalproviderid) {
        setSurveyState((prev: any) => ({
          ...prev,
          pharmacyId: item.clinicalproviderid,
        }));
      }
    }

    setValueTemp(selectedValue);
    onUpdate(selectedValue);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setHasSelectedItem(true); // 아이템을 선택했으므로 검색 재시작
  };

  return (
    <div className='t_QsTextarea form-control'>
      <div className="relative">
        <textarea
          ref={textAreaRef}
          {...omit(rest, propsToIgnore)}
          maxLength={isNaN(maxLength) ? undefined : maxLength}
          className={classNames(
            'textarea textarea-bordered w-full rounded-xl',
            className,
            error && 'textarea-error'
          )}
          onChange={(e) => {
            setValueTemp(e.target.value);
            // 사용자가 텍스트를 수정하면 선택된 아이템 상태를 리셋하여 검색을 다시 활성화
            setHasSelectedItem(false);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // 약간의 지연을 두어 클릭 이벤트가 처리될 수 있도록 함
            setTimeout(() => {
              setShowAutocomplete(false);
              setSelectedIndex(-1);
            }, 200);
          }}
          value={valueTemp}
        ></textarea>
        
        {/* 자동완성 목록 */}
        {showAutocomplete && (isPCPQuestion || isPharmacyQuestion) && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto transition-all duration-150 ease-in-out">
            {isLoading && (
              <div className="px-3 py-2 text-gray-500 text-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                  {sltStr({ key: 'qs-textarea-searching' })}
                </div>
              </div>
            )}
            {!isLoading && autocompleteItems.length > 0 && autocompleteItems.map((item, index) => (
              <div
                key={index}
                className={classNames(
                  'px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150',
                  index === selectedIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-50'
                )}
                onClick={() => handleSelectItem(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {isPCPQuestion && (
                  <>
                    <div className="font-medium text-gray-900">{item.name || item.providerName || sltStr({ key: 'qs-textarea-no-name' })}</div>
                    {item.address && (
                      <div className="text-sm text-gray-600 mt-1">{item.address} {item.city} {item.state}</div>
                    )}
                  </>
                )}
                {isPharmacyQuestion && (
                  <>
                    <div className="font-medium text-gray-900">
                      {
                        (() => {
                          // 약국명에서 마지막에 #12345와 같은 번호 패턴을 제거
                          const rawName = item.name || item.pharmacyName || sltStr({ key: 'qs-textarea-no-pharmacy' });
                          // 정규식: 공백 또는 아무 문자 뒤에 #숫+로 끝나는 부분을 제거
                          return rawName.replace(/\s*#\d+$/, '');
                        })()
                      }
                    </div>
                    {item.address && (
                      <div className="text-sm text-gray-600 mt-1">{item.address} {item.city} {item.state}</div>
                    )}
                  </>
                )}
              </div>
            ))}
            {!isLoading && autocompleteItems.length === 0 && searchDebounced && searchDebounced.length >= 2 && (
              <div className="px-3 py-2 text-gray-500 text-center">
                {sltStr({ key: 'qs-textarea-no-results' })}
              </div>
            )}
          </div>
        )}
      </div>
      
      {!isNaN(maxLength) && (
        <label className='label'>
          <span
            className={classNames(
              'label-text-alt',
              `${textLen}` === `${maxLength}` && 'text-red-500'
            )}
          >
            {textLen || 0} / {maxLength}
          </span>
        </label>
      )}
    </div>
  );
}

export default memo(QsTextarea);
